/**
 * The nextStep router — one function, one switch.
 *
 * THE rule: branch only on `nextStep`. Not on `userExists` (it is only
 * meaningful on `start`), not on which channel was typed, not on whether
 * `tokens` is present.
 */

import AUTH_SERVICES from "./authService";
import { completeAuth } from "./completeAuth";
import { setTokens } from "../../lib/api/tokenStore";
import { clearSignupSession, patchSignupSession, readSignupSession } from "./authSession";
import { defaultOtpChannel, detectChannelType } from "./authMappers";

export const AUTH_ROUTES = {
  getStarted: "/auth",
  verify: "/auth/verify",
  details: "/auth/details",
  photo: "/auth/photo",
  reactivate: "/auth/reactivate",
  home: "/",
};

const toVerify = (stage, { twoInputs = false, mode = "SESSION", legKey }) => ({
  route: AUTH_ROUTES.verify,
  stage,
  twoInputs,
  mode,
  legKey,
  authenticated: false,
});

/**
 * COMPLETE means tokens landed. Where the user goes depends on whether a
 * signup draft is in flight, NOT on `userExists`.
 *
 * With a draft this was a signup: store the tokens and hand off to Add Photo,
 * which calls completeAuth itself and then spends the draft on
 * `consumers/save/`. Without one it was a login, so finish here.
 */
const finishComplete = async (tokens) => {
  const session = readSignupSession();
  const draft = session?.profileDraft ?? null;

  if (draft) {
    setTokens({
      authToken: tokens.authToken,
      refreshToken: tokens.refreshToken,
    });

    return { route: AUTH_ROUTES.photo, authenticated: true };
  }

  await completeAuth(tokens);
  clearSignupSession();

  return { route: AUTH_ROUTES.home, authenticated: true };
};

/**
 * The written contract says `session/details/` answers VERIFY_CHANNEL_2 and
 * auto-fires the second OTP. The current deploy answers COMPLETE + tokens
 * instead, leaving channel 2 unverified — so when that happens we verify it
 * ourselves with the standalone OTP pair.
 *
 * Both branches have to exist: implement only COMPLETE and you break the day
 * they ship the fix; implement only VERIFY_CHANNEL_2 and you are broken now.
 */
const verifySecondChannelOurselves = async (tokens) => {
  const session = readSignupSession();
  const secondChannel = session?.detailsPayload?.secondChannelValue;

  if (!secondChannel) return finishComplete(tokens);

  setTokens({
    authToken: tokens.authToken,
    refreshToken: tokens.refreshToken,
  });

  try {
    const channelType = detectChannelType(secondChannel);

    const { data } = await AUTH_SERVICES.otpRequest({
      identityKey: secondChannel,
      channel: defaultOtpChannel(channelType),
    });

    if (data?.traceId) {
      patchSignupSession({ traceId: data.traceId, stage: "CHANNEL_2" });
      return toVerify("CHANNEL_2", { mode: "CONTACT", legKey: "leg-contact" });
    }
  } catch {
    // Deliberately swallowed. Signup must never dead-end on a verification
    // step we added ourselves.
  }

  return { route: AUTH_ROUTES.photo, authenticated: true };
};

/**
 * @param {object} response  a ConsumerAuthSessionResponse
 * @param {{origin?: "start"|"verify"|"details"}} options
 * @returns {Promise<object>} where to go next
 */
export const resolveNextStep = async (response, { origin } = {}) => {
  const { nextStep, sessionId, tokens } = response || {};

  // A resend on CHANNEL_1 re-calls `start`, which mints a NEW sessionId.
  // Adopt whatever came back; submitting the old one is a SESSION_EXPIRED.
  if (sessionId) patchSignupSession({ sessionId });

  switch (nextStep) {
    case "VERIFY_CHANNEL_1":
      patchSignupSession({ stage: "CHANNEL_1" });
      return toVerify("CHANNEL_1", { legKey: "leg-1" });

    // An existing user with two channels. Both OTPs have ALREADY fired, so
    // there is nothing to request and the form takes two codes.
    case "VERIFY_BOTH_CHANNELS":
      patchSignupSession({
        stage: "BOTH",
        secondaryChannel: response?.secondaryChannel ?? null,
      });
      return toVerify("BOTH", { twoInputs: true, legKey: "leg-both" });

    case "SIGNUP_DETAILS":
      patchSignupSession({ stage: "CHANNEL_1" });
      return { route: AUTH_ROUTES.details, authenticated: false };

    // A second OTP was auto-fired. Nothing to request, and this must be a
    // FRESH Verification instance — see the legKey note in Route.js.
    case "VERIFY_CHANNEL_2":
      patchSignupSession({ stage: "CHANNEL_2" });
      return toVerify("CHANNEL_2", { legKey: "leg-2" });

    case "COMPLETE":
      if (!tokens?.authToken) {
        clearSignupSession();
        return { route: AUTH_ROUTES.getStarted, authenticated: false };
      }

      return origin === "details"
        ? verifySecondChannelOurselves(tokens)
        : finishComplete(tokens);

    default:
      // An unrecognised step means the contract moved. Restarting is safer
      // than guessing a screen.
      clearSignupSession();
      return { route: AUTH_ROUTES.getStarted, authenticated: false };
  }
};
